/**
 * @file healthService.js
 * @description Service for monitoring system health, uptime, and infrastructure metrics.
 */
import api from "./apiClient";

const CACHE = {
    data: null,
    timePeriod: null,
    planFilter: null,
    customRangeKey: null,
    lastFetched: 0
};

/**
 * Fetches system health metrics from the API with caching.
 * 
 * @param {string} timePeriod - Time period for health data history.
 * @param {boolean} forceRefresh - Whether to bypass cache.
 * @returns {Promise<Object>} System health data incl. uptime, error rates, and infrastructure stats.
 */
export async function fetchSystemHealth(timePeriod = "weekly", planFilter = null, customRange = null, forceRefresh = false) {
    // Check cache (key needs to include customRange now)
    const now = Date.now();
    const cacheKey = customRange ? JSON.stringify(customRange) : "";
    if (
        !forceRefresh &&
        CACHE.data &&
        CACHE.timePeriod === timePeriod &&
        CACHE.planFilter === planFilter &&
        CACHE.customRangeKey === cacheKey &&
        now - CACHE.lastFetched < 60000 // 1 minute cache
    ) {
        console.log("⚡ Returning Cached Health Data");
        return CACHE.data;
    }

    try {
        const url = "https://utility.wiiz.it/api/admin/system-health/overview";

        const payload = {
            plan_filter: planFilter
        };

        if (timePeriod === "custom") {
            if (customRange?.start && customRange?.end) {
                payload.start_date = customRange.start;
                payload.end_date = customRange.end;
            } else {
                console.warn("Skipping Health API call: Custom range incomplete");
                return null;
            }
        } else {
            payload.time_period = timePeriod;
        }

        const response = await api.post(url, payload);

        const json = response.data;

        if (json.status !== "success" || !json.data) {
            throw new Error("Invalid API response format");
        }

        const data = json.data;

        // Cache the result
        // Cache the result
        CACHE.data = data;
        CACHE.timePeriod = timePeriod;
        CACHE.planFilter = planFilter;
        CACHE.customRangeKey = cacheKey;
        CACHE.lastFetched = now;

        return data;

    } catch (error) {
        console.error("Failed to fetch system health:", error);
        // Fallback or re-throw depending on app policy. 
        // For now, re-throwing so the UI can show generic error or handle it.
        // Or returning a minimal structure to prevent UI crash if that's preferred.
        throw error;
    }
}

/**
 * Fetches detailed metrics for a specific service.
 * 
 * @param {string} serviceName - Name of the service (e.g., "API Gateway").
 * @param {string} timePeriod - Time period for history.
 * @returns {Promise<Object>} Detailed service health data.
 */
export async function fetchServiceDetails(serviceName, timePeriod = "weekly") {
    try {
        const url = `https://utility.wiiz.it/api/admin/system-health/service/${encodeURIComponent(serviceName)}/details`;

        const response = await api.post(url, { time_period: timePeriod });

        const json = response.data;

        if (json.status !== "success" || !json.data) {
            throw new Error("Invalid API response format");
        }

        return json.data;

    } catch (error) {
        console.error(`Failed to fetch details for ${serviceName}:`, error);
        throw error;
    }
}
